<template>
  <!-- M-Pesa STK push from the company's own Paybill/Till, with every stage
       visible: number -> sending -> waiting for PIN -> paid / failed / no reply.
       Used by both checkouts. Can't be dismissed while it's working. -->
  <b-modal ref="modal" hide-footer hide-header :no-close-on-backdrop="true" :no-close-on-esc="true">
    <div class="text-center py-2">
      <h5 class="text-primary mb-1">M-Pesa payment</h5>
      <p class="mb-3">Amount <strong>{{ convertCurrencyToLocal(amount) }}</strong></p>

      <!-- 1. Number -->
      <form v-if="stage === 'phone'" @submit.prevent="send">
        <label class="d-block text-left">Customer's M-Pesa number</label>
        <vue-tel-input v-model="phone" :default-country="'KE'"></vue-tel-input>
        <small v-if="error" class="text-danger d-block text-left mt-2">{{ error }}</small>
        <button type="submit" class="btn btn-primary btn-block mt-3" :disabled="!phone">Send M-Pesa request</button>
        <button type="button" class="btn btn-outline-primary btn-block" @click="skip">Customer will pay directly (no prompt)</button>
        <button type="button" class="btn btn-outline-secondary btn-block" @click="cancel">Cancel</button>
      </form>

      <!-- 2. Sending -->
      <div v-else-if="stage === 'sending'" class="py-3">
        <div class="spinner-border text-primary mb-3" role="status"></div>
        <p class="mb-0">Sending the request to M-Pesa…</p>
      </div>

      <!-- 3. Waiting for the customer's PIN -->
      <div v-else-if="stage === 'waiting'" class="py-2">
        <div class="spinner-grow text-primary mb-3" role="status"></div>
        <p class="font-weight-bold mb-1">Prompt sent to {{ phone }}</p>
        <p class="mb-2">Ask the customer to check their phone and enter their M-Pesa PIN.</p>
        <p v-if="safaricomMessage" class="text-secondary small mb-2">M-Pesa: {{ safaricomMessage }}</p>
        <p class="text-secondary small mb-3">Waiting {{ elapsed }}s…</p>
        <button type="button" class="btn btn-outline-secondary btn-block" @click="reset">Change number</button>
        <button type="button" class="btn btn-outline-secondary btn-block" @click="cancel">Cancel and choose another method</button>
      </div>

      <!-- 4. No answer yet -->
      <div v-else-if="stage === 'timeout'" class="py-2">
        <h2 class="text-secondary mb-2"><i class="bi bi-hourglass-split"></i></h2>
        <p class="font-weight-bold mb-1">No response from the customer yet</p>
        <p class="mb-3">The prompt may have expired. Check again, or send a new prompt.</p>
        <button type="button" class="btn btn-primary btn-block" @click="checkAgain">Check again</button>
        <button type="button" class="btn btn-outline-primary btn-block" @click="send">Send a new prompt</button>
        <button type="button" class="btn btn-outline-secondary btn-block" @click="cancel">Cancel and choose another method</button>
      </div>

      <!-- 5. Paid -->
      <div v-else-if="stage === 'success'" class="py-2">
        <h1 class="text-primary mb-2"><i class="bi bi-check-circle-fill"></i></h1>
        <p class="font-weight-bold mb-1">Payment received</p>
        <p v-if="receipt" class="mb-1">M-Pesa receipt <strong>{{ receipt }}</strong></p>
        <p class="text-secondary small mb-0">{{ completing ? 'Completing the order…' : 'Done' }}</p>
      </div>

      <!-- 6. Failed -->
      <div v-else-if="stage === 'failed'" class="py-2">
        <h1 class="text-danger mb-2"><i class="bi bi-x-circle-fill"></i></h1>
        <p class="font-weight-bold mb-1">Payment not completed</p>
        <p class="text-danger mb-3">{{ error }}</p>
        <button v-if="!paidButUnrecorded" type="button" class="btn btn-primary btn-block" @click="send">Try again</button>
        <button v-if="!paidButUnrecorded" type="button" class="btn btn-outline-primary btn-block" @click="reset">Change number</button>
        <button type="button" class="btn btn-outline-secondary btn-block" @click="cancel">{{ paidButUnrecorded ? 'Close' : 'Choose another method' }}</button>
      </div>
    </div>
  </b-modal>
</template>

<script>
// Events:
//   paid({ transactionsValidatedId, receipt }) - parent records the payment, then
//       calls done() to close, or failCompletion(message) to show the problem
//   skip()   - no prompt: parent carries on as before (SMS validation etc.)
//   cancel() - parent goes back to choosing a payment method
export default {
  name: 'MpesaStkModal',

  props: {
    orderId: { type: [String, Number], default: null },
    amount: { type: Number, default: 0 },
    defaultPhone: { type: String, default: '' },
  },

  data() {
    return {
      stage: 'phone',
      phone: '',
      error: '',
      safaricomMessage: '',
      receipt: '',
      elapsed: 0,
      startedAt: null,
      completing: false,
      paidButUnrecorded: false,
      poll: null,
      clock: null,
    };
  },

  beforeDestroy() {
    this.stop();
  },

  methods: {
    open() {
      this.reset();
      if (!this.phone && this.defaultPhone) this.phone = this.defaultPhone;
      this.$refs.modal.show();
    },

    close() {
      this.stop();
      this.$refs.modal.hide();
    },

    // Parent finished recording the payment.
    done() {
      this.completing = false;
      setTimeout(() => this.close(), 1500);
    },

    // Money arrived but the order couldn't be updated - don't offer a retry.
    failCompletion(message) {
      this.completing = false;
      this.paidButUnrecorded = true;
      this.stage = 'failed';
      this.error = message;
    },

    reset() {
      this.stop();
      this.stage = 'phone';
      this.error = '';
      this.safaricomMessage = '';
      this.receipt = '';
      this.elapsed = 0;
      this.startedAt = null;
      this.completing = false;
      this.paidButUnrecorded = false;
    },

    cancel() {
      this.close();
      this.$emit('cancel');
    },

    skip() {
      this.close();
      this.$emit('skip');
    },

    async send() {
      if (!this.phone || !this.orderId) return;
      this.stop();
      this.stage = 'sending';
      this.error = '';
      this.safaricomMessage = '';
      try {
        const { data } = await axios.post('/api/process-order-payment', {
          method: 'M-pesa',
          orderData: { id: this.orderId, total_amount: this.amount, stkPhoneNumber: this.phone },
          amount: this.amount,
        });
        if (data.message === 'accepted for processing') {
          this.stage = 'waiting';
          this.startPolling();
        } else {
          this.stage = 'phone';
          this.error = data.message || 'The M-Pesa request could not be sent. Please try again.';
        }
      } catch (e) {
        this.stage = 'phone';
        this.error = e.response?.data?.message || 'The M-Pesa request could not be sent. Please try again.';
      }
    },

    checkAgain() {
      this.stage = 'waiting';
      this.startPolling(true);
    },

    // Every 3s, one check at a time. An unanswered prompt expires on the phone
    // after ~60s; after 90s we stop and let the cashier check again or resend.
    startPolling(keepClock = false) {
      this.stop();
      if (!keepClock || !this.startedAt) this.startedAt = Date.now();
      this.elapsed = Math.floor((Date.now() - this.startedAt) / 1000);
      this.clock = setInterval(() => {
        this.elapsed = Math.floor((Date.now() - this.startedAt) / 1000);
        if (this.elapsed >= 90 && this.stage === 'waiting') {
          this.stop();
          this.stage = 'timeout';
        }
      }, 1000);

      let busy = false;
      const check = async () => {
        if (busy) return;
        busy = true;
        try {
          const { data } = await axios.get('/api/validate-transaction', { params: { orderId: this.orderId } });
          const tx = data.transaction || {};
          if (tx.CustomerMessage || tx.ResponseDescription) {
            this.safaricomMessage = tx.CustomerMessage || tx.ResponseDescription;
          }
          if (data.message === 1) {
            this.stop();
            this.stage = 'success';
            this.receipt = data.description || tx.MpesaReceiptNumber || '';
            this.completing = true;
            this.$emit('paid', { transactionsValidatedId: tx.transactions_validated_id || null, receipt: this.receipt });
          } else if (data.message === 0) {
            this.stop();
            this.stage = 'failed';
            this.error = this.friendlyError(data.description);
          }
        } catch (e) {
          console.error('Error checking M-Pesa prompt:', e);
        } finally {
          busy = false;
        }
      };
      check();
      this.poll = setInterval(check, 3000);
    },

    stop() {
      if (this.poll) { clearInterval(this.poll); this.poll = null; }
      if (this.clock) { clearInterval(this.clock); this.clock = null; }
    },

    // Safaricom's result texts, in words a cashier can act on.
    friendlyError(desc) {
      const d = String(desc || '');
      if (/cancel/i.test(d)) return 'The customer cancelled the M-Pesa prompt.';
      if (/insufficient|balance/i.test(d)) return 'The customer does not have enough money in M-Pesa.';
      if (/timeout|expired|unable to lock|DS timeout/i.test(d)) return 'The prompt timed out before the customer entered their PIN.';
      if (/initiator|wrong pin|invalid pin/i.test(d)) return 'The customer entered the wrong M-Pesa PIN.';
      return d || 'M-Pesa did not complete the payment.';
    },
  },
};
</script>
